import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ApiError, api, unwrap } from "../api";
import {
  type BrandFont,
  DEFAULT_BRAND_COLOR,
  brandPalette,
  brandTypography,
  readFontFile,
  readLogoFile,
  useBrandFonts,
  useCompanyIdentity,
} from "../brand";
import {
  Button,
  Empty,
  ErrorNote,
  Field,
  Loading,
  Panel,
  Table,
  TableSearch,
  Td,
  Th,
  input,
  useTable,
} from "../ui";
import { ProposalTemplatePanel } from "./contract-template";
import { PriceBookPanel, ProductTypesPanel, WorkStagesPanel } from "./estimating";
import { IntegrationsPanel } from "./integrations";

const ROLES = [
  ["viewer", "Viewer", "Read-only."],
  ["accountant", "Accountant", "Records costs, bills, and payments."],
  ["manager", "Manager", "Runs projects and billing."],
  ["admin", "Admin", "Manages people and settings."],
  ["owner", "Owner", "Everything, including other owners."],
] as const;

const SETTINGS_TABS = [
  ["company", "Company"],
  ["documents", "Documents"],
  ["people", "People"],
  ["activity", "Activity"],
  ["estimating", "Estimating"],
  ["integrations", "Integrations"],
  ["deleted", "Deleted"],
] as const;

type SettingsTab = (typeof SETTINGS_TABS)[number][0];

function roleLabel(role: string) {
  return ROLES.find(([v]) => v === role)?.[1] ?? role;
}

function when(iso: string | Date) {
  return new Date(iso).toISOString().slice(0, 16).replace("T", " ");
}

/**
 * The chosen color in the two places a document actually uses it — as a filled
 * table header and as heading text on white. A brand color that looks fine as
 * a swatch can still be unreadable as one of those, and this shows which
 * before anyone sends a proposal out.
 */
function BrandPreview({
  color,
  display,
  body,
}: {
  color: string;
  display: BrandFont | null;
  body: BrandFont | null;
}) {
  const palette = brandPalette(color);
  // The faces are registered against the same fixed family names the document
  // uses, so what this shows is literally what will print — not an
  // approximation maintained alongside it.
  const typography = brandTypography({
    displayFontSrc: display?.src ?? null,
    bodyFontSrc: body?.src ?? null,
  });
  useBrandFonts(typography);

  return (
    <div className="overflow-hidden rounded-lg border border-neutral-200">
      <div
        className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide"
        style={{ backgroundColor: palette.base, color: palette.onBase }}
      >
        Investment
      </div>
      <div
        className="bg-white px-3 pt-2 text-sm font-semibold"
        style={{
          color: palette.ink,
          ...(typography.display && { fontFamily: typography.display }),
        }}
      >
        Payment schedule
      </div>
      <div
        className="bg-white px-3 pb-2 text-sm text-neutral-700"
        style={typography.body ? { fontFamily: typography.body } : undefined}
      >
        Deposit on signing — 50% of the contract.
      </div>
    </div>
  );
}

/**
 * One typography slot. Not a `Field`: that wraps its children in a `<label>`,
 * and a click on Remove would be forwarded to the file input on the way out,
 * reopening the picker the moment you cleared it.
 */
function FontSlot({
  label,
  hint,
  font,
  disabled,
  onPick,
  onClear,
}: {
  label: string;
  hint: string;
  font: BrandFont | null;
  disabled: boolean;
  onPick: (file: File | undefined) => void;
  onClear: () => void;
}) {
  return (
    <div className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-neutral-500">
        {label}
      </span>
      <div className="flex items-center gap-3">
        <input
          type="file"
          aria-label={label}
          accept=".woff2,.woff,.ttf,.otf,font/woff2,font/woff,font/ttf,font/otf"
          disabled={disabled}
          className="min-w-0 text-sm text-neutral-500 file:mr-3 file:rounded-lg file:border file:border-neutral-300 file:bg-white file:px-3 file:py-1.5 file:text-sm file:text-neutral-700"
          onChange={(e) => {
            onPick(e.target.files?.[0]);
            // Cleared so re-picking the same file after a failure still fires.
            e.target.value = "";
          }}
        />
        {font && !disabled && (
          <button
            type="button"
            className="shrink-0 text-sm text-neutral-500 underline underline-offset-2 hover:text-neutral-900"
            onClick={onClear}
          >
            Remove
          </button>
        )}
      </div>
      <span className="mt-1 block text-xs text-neutral-500">
        {font ? `${font.name} · ` : ""}
        {hint} WOFF2 keeps proposals quick to open.
      </span>
    </div>
  );
}

export function Settings({
  slug,
  role,
  currency,
}: {
  slug: string;
  role: string;
  currency: string;
}) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<{ email: string; link: string; mailed: boolean } | null>(
    null,
  );
  const [copied, setCopied] = useState(false);

  const isAdmin = role === "admin" || role === "owner";
  const isOwner = role === "owner";

  const visibleTabs = SETTINGS_TABS.filter(
    ([id]) => (id !== "activity" || isAdmin) && (id !== "deleted" || isOwner),
  );
  const [tab, setTab] = useState<SettingsTab>("company");
  const activeTab = visibleTabs.some(([id]) => id === tab) ? tab : "company";

  const [docKind, setDocKind] = useState<"proposal" | "contract">("proposal");

  const company = useCompanyIdentity(slug);
  const [companyDirty, setCompanyDirty] = useState(false);
  const [companyLegalName, setCompanyLegalName] = useState("");
  const [companyEin, setCompanyEin] = useState("");
  const [companyPhone, setCompanyPhone] = useState("");
  const [companyEmail, setCompanyEmail] = useState("");
  const [companyBrandColor, setCompanyBrandColor] = useState("");
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);
  const [companyDisplayFont, setCompanyDisplayFont] = useState<BrandFont | null>(null);
  const [companyBodyFont, setCompanyBodyFont] = useState<BrandFont | null>(null);

  // Loaded once per fetch, same as any other edit form here — guarded on
  // `companyDirty` so a background refetch can't clobber something half-typed.
  useEffect(() => {
    if (!company.data || companyDirty) return;
    setCompanyLegalName(company.data.company.legalName ?? "");
    setCompanyEin(company.data.company.ein ?? "");
    setCompanyPhone(company.data.company.phone ?? "");
    setCompanyEmail(company.data.company.email ?? "");
    setCompanyBrandColor(company.data.company.brandColor ?? "");
    setCompanyLogo(company.data.company.logoUrl ?? null);
    const c = company.data.company;
    setCompanyDisplayFont(
      c.displayFontSrc ? { src: c.displayFontSrc, name: c.displayFontName ?? "Display" } : null,
    );
    setCompanyBodyFont(
      c.bodyFontSrc ? { src: c.bodyFontSrc, name: c.bodyFontName ?? "Body" } : null,
    );
  }, [company.data, companyDirty]);

  const saveCompany = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.api.c[":slug"].$patch({
          param: { slug },
          json: {
            legalName: companyLegalName,
            ein: companyEin,
            phone: companyPhone,
            email: companyEmail,
            brandColor: companyBrandColor,
            logoUrl: companyLogo ?? "",
            displayFontName: companyDisplayFont?.name ?? "",
            displayFontSrc: companyDisplayFont?.src ?? "",
            bodyFontName: companyBodyFont?.name ?? "",
            bodyFontSrc: companyBodyFont?.src ?? "",
          } as never,
        }),
      ),
    onSuccess: () => {
      setCompanyDirty(false);
      qc.invalidateQueries({ queryKey: ["company", slug] });
      qc.invalidateQueries({ queryKey: ["me"] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : "Couldn't update company info."),
  });

  // Re-encoded to a bounded data URI here rather than uploaded: there is no
  // object store bound (see CLAUDE.md), and an inline logo is also the one
  // that always survives the proposal's PDF export.
  async function pickLogo(file: File | undefined) {
    if (!file) return;
    setError(null);
    try {
      setCompanyLogo(await readLogoFile(file));
      setCompanyDirty(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't read that image.");
    }
  }

  // Embedded whole rather than re-encoded: a browser can't subset or convert a
  // font, so the only lever on size is refusing one that's too big. Which is
  // also why the hint below asks for WOFF2.
  async function pickFont(slot: "display" | "body", file: File | undefined) {
    if (!file) return;
    setError(null);
    try {
      const font = await readFontFile(file);
      if (slot === "display") setCompanyDisplayFont(font);
      else setCompanyBodyFont(font);
      setCompanyDirty(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't read that font.");
    }
  }

  const members = useQuery({
    queryKey: ["members", slug],
    queryFn: async () => unwrap(await api.api.c[":slug"].members.$get({ param: { slug } })),
  });
  const invites = useQuery({
    queryKey: ["invitations", slug],
    enabled: isAdmin,
    queryFn: async () => unwrap(await api.api.c[":slug"].invitations.$get({ param: { slug } })),
  });
  const auditEntries = useQuery({
    queryKey: ["audit", slug],
    enabled: isAdmin,
    queryFn: async () =>
      unwrap(await api.api.c[":slug"].audit.$get({ param: { slug }, query: {} })),
  });
  const deleted = useQuery({
    queryKey: ["trash", slug],
    enabled: isOwner,
    queryFn: async () => unwrap(await api.api.c[":slug"].trash.$get({ param: { slug } })),
  });

  const membersTable = useTable(members.data?.members ?? [], {
    filter: (m, q) =>
      `${m.email} ${m.name ?? ""} ${roleLabel(m.role)}`.toLowerCase().includes(q.toLowerCase()),
    sorters: {
      email: (m) => m.email.toLowerCase(),
      name: (m) => (m.name ?? "").toLowerCase(),
      role: (m) => roleLabel(m.role).toLowerCase(),
      joined: (m) => new Date(m.joinedAt).getTime(),
    },
  });

  const invitesTable = useTable(invites.data?.invitations ?? [], {
    filter: (i, q) =>
      `${i.email} ${roleLabel(i.role)} ${i.invitedBy ?? ""}`
        .toLowerCase()
        .includes(q.toLowerCase()),
    sorters: {
      email: (i) => i.email.toLowerCase(),
      role: (i) => roleLabel(i.role).toLowerCase(),
      status: (i) =>
        i.acceptedAt
          ? "accepted"
          : !i.acceptedAt && new Date(i.expiresAt) < new Date()
            ? "expired"
            : `expires ${when(i.expiresAt)}`,
      invitedBy: (i) => (i.invitedBy ?? "").toLowerCase(),
    },
  });

  const auditTable = useTable(auditEntries.data?.entries ?? [], {
    filter: (e, q) =>
      `${e.actorEmail ?? ""} ${e.entityType} ${e.action}`.toLowerCase().includes(q.toLowerCase()),
    sorters: {
      when: (e) => e.at,
      who: (e) => (e.actorEmail ?? "").toLowerCase(),
      what: (e) => `${e.entityType} ${e.action}`.toLowerCase(),
    },
  });

  function refresh() {
    for (const k of ["members", "invitations", "audit", "trash"]) {
      qc.invalidateQueries({ queryKey: [k, slug] });
    }
  }

  const run = useMutation({
    mutationFn: async (fn: () => Promise<unknown>) => fn(),
    onMutate: () => setError(null),
    onSuccess: refresh,
    onError: (e) =>
      setError(e instanceof ApiError ? e.message : "Something went wrong. Try again."),
  });

  /**
   * Putting a record back moves money on the ledger, so this drops the whole
   * cache rather than naming queries: any figure on any page could have been
   * the one that changed.
   */
  const restore = useMutation({
    mutationFn: async (id: string) =>
      unwrap(await api.api.c[":slug"].trash[":id"].restore.$post({ param: { slug, id } })),
    onMutate: () => setError(null),
    onSuccess: () => qc.invalidateQueries(),
    onError: (e) =>
      setError(e instanceof ApiError ? e.message : "Couldn't restore that. Try again."),
  });

  const invite = useMutation({
    mutationFn: async (body: { email: string; role: string }) =>
      unwrap(await api.api.c[":slug"].invitations.$post({ param: { slug }, json: body as never })),
    onMutate: () => {
      setError(null);
      setIssued(null);
      setCopied(false);
    },
    onSuccess: (data) => {
      // Shown even when the mail was sent: delivery is best-effort and an
      // admin who can see the link is never stuck waiting on something they
      // cannot observe — a mistyped address included.
      setIssued({
        email: data.invitation?.email ?? "",
        link: data.link ?? `${window.location.origin}/invite/${data.token}`,
        mailed: data.mailed === true,
      });
      refresh();
    },
    onError: (e) =>
      setError(e instanceof ApiError ? e.message : "Couldn't create that invitation."),
  });

  if (members.isPending) return <Loading />;

  const owners = members.data?.members.filter((m) => m.role === "owner").length ?? 0;

  return (
    <>
      {error && <ErrorNote>{error}</ErrorNote>}

      <nav className="mb-6 flex flex-wrap gap-x-1 border-b border-neutral-200">
        {visibleTabs.map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            aria-current={activeTab === id ? "page" : undefined}
            className={`-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm transition-colors ${
              activeTab === id
                ? "border-accent-500 font-medium text-neutral-900"
                : "border-transparent text-neutral-500 hover:text-neutral-900"
            }`}
          >
            {label}
          </button>
        ))}
      </nav>

      {activeTab === "company" && (
        <Panel title="Company">
          {!isAdmin && (
            <p className="mb-3 max-w-prose text-sm text-neutral-500">
              Identity and branding shown on proposals. Changing them needs the admin role.
            </p>
          )}
          {company.isPending ? (
            <Loading />
          ) : (
            <form
              className="grid gap-3 sm:grid-cols-3"
              onSubmit={(e) => {
                e.preventDefault();
                saveCompany.mutate();
              }}
            >
              <Field
                label="Legal name"
                hint={`Names the party bound by a signed proposal. Blank uses "${company.data?.company.name ?? ""}".`}
              >
                <input
                  className={input}
                  value={companyLegalName}
                  disabled={!isAdmin}
                  onChange={(e) => {
                    setCompanyLegalName(e.target.value);
                    setCompanyDirty(true);
                  }}
                  placeholder="Acme Builders LLC"
                />
              </Field>
              <Field label="EIN">
                <input
                  className={input}
                  value={companyEin}
                  disabled={!isAdmin}
                  onChange={(e) => {
                    setCompanyEin(e.target.value);
                    setCompanyDirty(true);
                  }}
                  placeholder="12-3456789"
                />
              </Field>
              <div />
              <Field label="Phone">
                <input
                  className={input}
                  value={companyPhone}
                  disabled={!isAdmin}
                  onChange={(e) => {
                    setCompanyPhone(e.target.value);
                    setCompanyDirty(true);
                  }}
                  placeholder="(555) 555-0100"
                />
              </Field>
              <Field label="Email">
                <input
                  className={input}
                  type="email"
                  value={companyEmail}
                  disabled={!isAdmin}
                  onChange={(e) => {
                    setCompanyEmail(e.target.value);
                    setCompanyDirty(true);
                  }}
                  placeholder="office@example.com"
                />
              </Field>
              <div />

              <Field label="Brand color" hint="Used on documents only, never on this app.">
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    aria-label="Brand color"
                    className="h-9 w-12 shrink-0 cursor-pointer rounded-lg border border-neutral-300 bg-white p-1 disabled:cursor-default"
                    value={companyBrandColor || DEFAULT_BRAND_COLOR}
                    disabled={!isAdmin}
                    onChange={(e) => {
                      setCompanyBrandColor(e.target.value);
                      setCompanyDirty(true);
                    }}
                  />
                  <input
                    className={input}
                    value={companyBrandColor}
                    disabled={!isAdmin}
                    onChange={(e) => {
                      setCompanyBrandColor(e.target.value);
                      setCompanyDirty(true);
                    }}
                    placeholder={DEFAULT_BRAND_COLOR}
                  />
                </div>
              </Field>

              {/* Not a `Field`: that wraps its children in a <label>, and a click
                on Remove would be forwarded to the file input and open the
                picker on the way out. */}
              <div className="block">
                <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-neutral-500">
                  Logo
                </span>
                <div className="flex items-center gap-3">
                  {companyLogo && (
                    <img
                      src={companyLogo}
                      alt="Company logo"
                      className="h-9 w-auto max-w-24 shrink-0 object-contain"
                    />
                  )}
                  <input
                    type="file"
                    aria-label="Logo"
                    accept="image/png,image/jpeg,image/webp"
                    disabled={!isAdmin}
                    className="min-w-0 text-sm text-neutral-500 file:mr-3 file:rounded-lg file:border file:border-neutral-300 file:bg-white file:px-3 file:py-1.5 file:text-sm file:text-neutral-700"
                    onChange={(e) => {
                      pickLogo(e.target.files?.[0]);
                      // Cleared so re-picking the same file after a failure still fires.
                      e.target.value = "";
                    }}
                  />
                  {companyLogo && isAdmin && (
                    <button
                      type="button"
                      className="shrink-0 text-sm text-neutral-500 underline underline-offset-2 hover:text-neutral-900"
                      onClick={() => {
                        setCompanyLogo(null);
                        setCompanyDirty(true);
                      }}
                    >
                      Remove
                    </button>
                  )}
                </div>
                <span className="mt-1 block text-xs text-neutral-500">
                  PNG or JPEG. Shrunk to fit a letterhead.
                </span>
              </div>

              <FontSlot
                label="Display font"
                hint="Headings and the letterhead."
                font={companyDisplayFont}
                disabled={!isAdmin}
                onPick={(file) => pickFont("display", file)}
                onClear={() => {
                  setCompanyDisplayFont(null);
                  setCompanyDirty(true);
                }}
              />

              <FontSlot
                label="Body font"
                hint="Running text and the money tables."
                font={companyBodyFont}
                disabled={!isAdmin}
                onPick={(file) => pickFont("body", file)}
                onClear={() => {
                  setCompanyBodyFont(null);
                  setCompanyDirty(true);
                }}
              />

              <Field label="On a document">
                <BrandPreview
                  color={companyBrandColor}
                  display={companyDisplayFont}
                  body={companyBodyFont}
                />
              </Field>

              {isAdmin && (
                <div className="flex items-end">
                  <Button type="submit" disabled={!companyDirty || saveCompany.isPending}>
                    {saveCompany.isPending ? "Saving…" : companyDirty ? "Save" : "Saved"}
                  </Button>
                </div>
              )}
            </form>
          )}
        </Panel>
      )}

      {activeTab === "documents" && (
        <>
          <div className="mb-4 flex gap-1 rounded-lg bg-neutral-100 p-1 w-fit">
            {(["proposal", "contract"] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setDocKind(k)}
                aria-current={docKind === k ? "page" : undefined}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  docKind === k
                    ? "bg-white text-neutral-900 shadow-sm"
                    : "text-neutral-500 hover:text-neutral-900"
                }`}
              >
                {k === "proposal" ? "Proposal" : "Contract"}
              </button>
            ))}
          </div>
          {docKind === "proposal" ? (
            <ProposalTemplatePanel slug={slug} kind="proposal" isAdmin={isAdmin} />
          ) : (
            <ProposalTemplatePanel slug={slug} kind="contract" isAdmin={isAdmin} />
          )}
        </>
      )}

      {activeTab === "people" && (
        <>
          <Panel title="People">
            {!isAdmin && (
              <p className="mb-3 max-w-prose text-sm text-neutral-500">
                You can see who's here. Changing roles or inviting anyone needs the admin role.
              </p>
            )}

            <TableSearch
              value={membersTable.query}
              onChange={membersTable.setQuery}
              placeholder="Filter people…"
            />
            <Table
              head={
                <>
                  <Th
                    sortKey="email"
                    active={membersTable.sortKey === "email"}
                    dir={membersTable.dir}
                    onSort={membersTable.toggleSort}
                  >
                    Email
                  </Th>
                  <Th
                    sortKey="name"
                    active={membersTable.sortKey === "name"}
                    dir={membersTable.dir}
                    onSort={membersTable.toggleSort}
                  >
                    Name
                  </Th>
                  <Th
                    sortKey="role"
                    active={membersTable.sortKey === "role"}
                    dir={membersTable.dir}
                    onSort={membersTable.toggleSort}
                  >
                    Role
                  </Th>
                  <Th
                    sortKey="joined"
                    active={membersTable.sortKey === "joined"}
                    dir={membersTable.dir}
                    onSort={membersTable.toggleSort}
                  >
                    Joined
                  </Th>
                  <Th />
                </>
              }
            >
              {membersTable.rows.map((m) => {
                // The last owner cannot be demoted or removed: a company with no
                // owner is locked out of its own settings permanently.
                const lastOwner = m.role === "owner" && owners === 1;
                return (
                  <tr
                    key={m.id}
                    className="border-b border-neutral-100 even:bg-neutral-50 transition-colors hover:bg-neutral-100"
                  >
                    <Td>{m.email}</Td>
                    <Td muted>{m.name || "—"}</Td>
                    <Td>
                      {isAdmin && !lastOwner ? (
                        <select
                          className={`${input} w-40`}
                          value={m.role}
                          disabled={run.isPending}
                          onChange={(e) =>
                            run.mutate(() =>
                              unwrap(
                                api.api.c[":slug"].members[":id"].$patch({
                                  param: { slug, id: m.id },
                                  json: { role: e.target.value as never },
                                }),
                              ),
                            )
                          }
                        >
                          {ROLES.map(([v, label]) => (
                            <option key={v} value={v}>
                              {label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="text-neutral-600">
                          {roleLabel(m.role)}
                          {lastOwner && (
                            <span className="ml-2 text-xs text-neutral-400">last owner</span>
                          )}
                        </span>
                      )}
                    </Td>
                    <Td mono muted>
                      {when(m.joinedAt)}
                    </Td>
                    <Td right>
                      {isAdmin && !lastOwner && (
                        <Button
                          variant="ghost"
                          disabled={run.isPending}
                          onClick={() =>
                            run.mutate(() =>
                              unwrap(
                                api.api.c[":slug"].members[":id"].$delete({
                                  param: { slug, id: m.id },
                                }),
                              ),
                            )
                          }
                        >
                          Remove
                        </Button>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </Table>
          </Panel>

          {isAdmin && (
            <Panel title="Invitations">
              <p className="mb-3 max-w-prose text-sm text-neutral-500">
                Sign-up is invitation only. The link works once, expires in 14 days, and only for
                the address it was issued to — so a forwarded link can't be used by someone else.
              </p>

              <InviteForm
                busy={invite.isPending}
                canInviteOwner={role === "owner"}
                onSubmit={(body) => invite.mutate(body)}
              />

              {issued && (
                <div className="mb-6 rounded-xl border border-accent-500/40 bg-accent-50/50 p-5 shadow-sm">
                  <p className="text-sm">
                    Invitation for <span className="font-medium">{issued.email}</span>.{" "}
                    {issued.mailed
                      ? "An email is on its way. Here's the link too, in case it doesn't arrive — it won't be shown again."
                      : "Mail isn't configured, so send them this link yourself — it won't be shown again."}
                  </p>
                  <div className="mt-2 flex gap-2">
                    <input className={input} readOnly value={issued.link} />
                    <Button
                      onClick={async () => {
                        await navigator.clipboard.writeText(issued.link);
                        setCopied(true);
                      }}
                    >
                      {copied ? "Copied" : "Copy"}
                    </Button>
                  </div>
                </div>
              )}

              {!invites.data?.invitations.length ? (
                <Empty title="No invitations" />
              ) : (
                <>
                  <TableSearch
                    value={invitesTable.query}
                    onChange={invitesTable.setQuery}
                    placeholder="Filter invitations…"
                  />
                  <Table
                    head={
                      <>
                        <Th
                          sortKey="email"
                          active={invitesTable.sortKey === "email"}
                          dir={invitesTable.dir}
                          onSort={invitesTable.toggleSort}
                        >
                          Email
                        </Th>
                        <Th
                          sortKey="role"
                          active={invitesTable.sortKey === "role"}
                          dir={invitesTable.dir}
                          onSort={invitesTable.toggleSort}
                        >
                          Role
                        </Th>
                        <Th
                          sortKey="status"
                          active={invitesTable.sortKey === "status"}
                          dir={invitesTable.dir}
                          onSort={invitesTable.toggleSort}
                        >
                          Status
                        </Th>
                        <Th
                          sortKey="invitedBy"
                          active={invitesTable.sortKey === "invitedBy"}
                          dir={invitesTable.dir}
                          onSort={invitesTable.toggleSort}
                        >
                          Invited by
                        </Th>
                        <Th />
                      </>
                    }
                  >
                    {invitesTable.rows.map((i) => {
                      const expired = !i.acceptedAt && new Date(i.expiresAt) < new Date();
                      return (
                        <tr
                          key={i.id}
                          className="border-b border-neutral-100 even:bg-neutral-50 transition-colors hover:bg-neutral-100"
                        >
                          <Td>{i.email}</Td>
                          <Td muted>{roleLabel(i.role)}</Td>
                          <Td muted>
                            {i.acceptedAt
                              ? "accepted"
                              : expired
                                ? "expired"
                                : `expires ${when(i.expiresAt)}`}
                          </Td>
                          <Td muted>{i.invitedBy ?? "—"}</Td>
                          <Td right>
                            {!i.acceptedAt && (
                              <Button
                                variant="ghost"
                                disabled={run.isPending}
                                onClick={() =>
                                  run.mutate(() =>
                                    unwrap(
                                      api.api.c[":slug"].invitations[":id"].revoke.$post({
                                        param: { slug, id: i.id },
                                      }),
                                    ),
                                  )
                                }
                              >
                                Revoke
                              </Button>
                            )}
                          </Td>
                        </tr>
                      );
                    })}
                  </Table>
                </>
              )}
            </Panel>
          )}
        </>
      )}

      {activeTab === "activity" && isAdmin && (
        <Panel title="Activity">
          <p className="mb-3 max-w-prose text-sm text-neutral-500">
            Who changed access, and when. Money movements are recorded separately on the ledger,
            which is append-only — a correction there is a reversing entry, never an edit.
          </p>

          {auditEntries.isPending ? (
            <Loading />
          ) : !auditEntries.data?.entries.length ? (
            <Empty title="Nothing recorded yet" />
          ) : (
            <>
              <TableSearch
                value={auditTable.query}
                onChange={auditTable.setQuery}
                placeholder="Filter activity…"
              />
              <Table
                head={
                  <>
                    <Th
                      sortKey="when"
                      active={auditTable.sortKey === "when"}
                      dir={auditTable.dir}
                      onSort={auditTable.toggleSort}
                    >
                      When
                    </Th>
                    <Th
                      sortKey="who"
                      active={auditTable.sortKey === "who"}
                      dir={auditTable.dir}
                      onSort={auditTable.toggleSort}
                    >
                      Who
                    </Th>
                    <Th
                      sortKey="what"
                      active={auditTable.sortKey === "what"}
                      dir={auditTable.dir}
                      onSort={auditTable.toggleSort}
                    >
                      What
                    </Th>
                    <Th>Detail</Th>
                  </>
                }
              >
                {auditTable.rows.map((e) => (
                  <tr
                    key={e.id}
                    className="border-b border-neutral-100 even:bg-neutral-50 transition-colors hover:bg-neutral-100"
                  >
                    <Td mono muted>
                      {when(e.at)}
                    </Td>
                    <Td muted>{e.actorEmail ?? "—"}</Td>
                    <Td>
                      {e.entityType} {e.action.replace(/_/g, " ")}
                    </Td>
                    <Td muted>
                      {e.diff ? <code className="text-xs">{JSON.stringify(e.diff)}</code> : "—"}
                    </Td>
                  </tr>
                ))}
              </Table>
            </>
          )}
        </Panel>
      )}

      {activeTab === "deleted" && isOwner && (
        <Panel title="Deleted">
          <p className="mb-3 max-w-prose text-sm text-neutral-500">
            Nothing here is gone. A deleted record leaves every list and whatever it put on the
            ledger is reversed, but the row and the reversal both stay — so restoring puts back the
            record and the figures together. Deleting a project takes its documents with it; they
            come back with it too.
          </p>

          {deleted.isPending ? (
            <Loading />
          ) : !deleted.data?.deletions.length ? (
            <Empty title="Nothing deleted" />
          ) : (
            <Table
              head={
                <>
                  <Th>What</Th>
                  <Th>Deleted</Th>
                  <Th>By</Th>
                  <Th />
                </>
              }
            >
              {deleted.data.deletions.map((d) => (
                <tr
                  key={d.id}
                  className="border-b border-neutral-100 even:bg-neutral-50 transition-colors hover:bg-neutral-100"
                >
                  <Td>
                    {d.label}
                    {d.alsoDeleted > 0 && (
                      <span className="ml-2 text-xs text-neutral-500">
                        and {d.alsoDeleted} record{d.alsoDeleted === 1 ? "" : "s"} with it
                      </span>
                    )}
                  </Td>
                  <Td mono muted>
                    {when(d.deletedAt)}
                  </Td>
                  <Td muted>{d.deletedBy ?? "—"}</Td>
                  <Td right>
                    <Button
                      variant="ghost"
                      disabled={restore.isPending}
                      onClick={() => restore.mutate(d.id)}
                    >
                      Restore
                    </Button>
                  </Td>
                </tr>
              ))}
            </Table>
          )}
        </Panel>
      )}

      {activeTab === "estimating" && (
        <>
          <WorkStagesPanel slug={slug} role={role} />
          <ProductTypesPanel slug={slug} role={role} />
          <PriceBookPanel slug={slug} role={role} side="sell" currency={currency} />
        </>
      )}

      {activeTab === "integrations" && <IntegrationsPanel slug={slug} isOwner={isOwner} />}
    </>
  );
}

function InviteForm({
  busy,
  canInviteOwner,
  onSubmit,
}: {
  busy: boolean;
  canInviteOwner: boolean;
  onSubmit: (body: { email: string; role: string }) => void;
}) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("viewer");

  const available = ROLES.filter(([v]) => canInviteOwner || v !== "owner");

  return (
    <form
      className="mb-6 rounded-xl border border-neutral-200 bg-white p-5 shadow-sm"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({ email, role });
        setEmail("");
      }}
    >
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Email">
          <input
            className={input}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="them@example.com"
            required
          />
        </Field>
        <Field label="Role" hint={ROLES.find(([v]) => v === role)?.[2]}>
          <select className={input} value={role} onChange={(e) => setRole(e.target.value)}>
            {available.map(([v, label]) => (
              <option key={v} value={v}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        <div className="flex items-center">
          <Button type="submit" disabled={busy}>
            {busy ? "Creating…" : "Create invite link"}
          </Button>
        </div>
      </div>
    </form>
  );
}
